/**
 * tracking-persistence.js
 */

import axios from 'axios';
import cloneDeep from 'lodash.clonedeep';
import { format, differenceInSeconds } from 'date-fns';
import * as Sentry from "@sentry/react";
import { Types } from 'utils/constants';
import { sleep } from 'utils/misc';
import { objectToFormData } from "utils/form-data";
import { DISTANCE_UNIT_NAME, DISTANCE_UNIT_DDV, convertDistance, getTrackDistanceM } from 'utils/distance';
import { TRACK_TYPE } from './tracking-common';  


const sleepTime = 1000;


const DELETE_RECORD_STATUS = {
  SUCCESS: 'success',
  FAIL_STOP: 'fail-stop',
  FAIL_RETRY: 'fail-retry',
};


const trackModel = (track, device) => {
  return {
    dateTimeCreated: track.dateTimeCreated,
    latitude: track.latitude,
    longitude: track.longitude,
    appLocationConfigId: device.appLocationConfigId,
    device: {
      brand: device.brand,
      model: device.model,
      platform: device.platform,
      uniqueId: device.uniqueId,
      appVersion: device.appVersion,
      canBackgroundTrack: device.canBackgroundTrack
    }
  };
}


const deploymentTrackModel = (dogId, track, device) => {
  const model = trackModel(track, device);
  model.dogId = dogId;
  return model;
}


const leadTrackModel = (name, track, device) => {
  const model = trackModel(track, device);
  model.name = name;
  return model;
}


const followTrackModel = (leadTrackId, exerciseId, dogId, isBlind, track, device) => {
  const model = trackModel(track, device);
  model.exerciseId = exerciseId;
  model.leadTrackId = leadTrackId;
  model.isBlind = isBlind;
  model.dogId = dogId;
  return model;
}


const getBearing = (point1, point2) => {
  const y = Math.sin(point2.longitude - point1.longitude) * Math.cos(point2.latitude);
  const x = Math.cos(point1.latitude) * Math.sin(point2.latitude) - Math.sin(point1.latitude) * Math.cos(point2.latitude) * Math.cos(point2.longitude - point1.longitude);
  return Math.atan2(y, x) * 180 / Math.PI;
}


const getDirection = (bearing) => {
  if (bearing >= 45 && bearing <= 135) {
    return 'east';
  } else if (bearing > 135 && bearing <= 225) {
    return 'south';
  } else if (bearing > 225 && bearing <= 315) {
    return 'west';
  } else {
    return 'north';
  }
}


const getTurnCount = (points) => {
  let turnCount = 0;
  let shortDirection = null;
  let longDirection = null;
  let shortDirectionCount = 0;
  
  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    const prevPoint = points[i - 1];
    const direction = getDirection(getBearing(prevPoint, point));
    if (direction !== shortDirection) {
      shortDirection = direction;
      shortDirectionCount = 1;
    } else {
      shortDirectionCount++;

      if (shortDirectionCount === 5) {
        if (longDirection && longDirection !== shortDirection) {
          turnCount++;
        }
        longDirection = shortDirection;
      }
    }
  }

  return turnCount;
}


// extends o1 with props from o2 without overwriting existing
const extend = (o1, o2) => {
  for (let prop in o2) {
    if (!o1.hasOwnProperty(prop)) {
      o1[prop] = o2[prop];
    }
  }
}


const isPersisted= (track) => {
  var id = track?.id;
  var fId = parseFloat(id);
  return !isNaN(id) && (fId | 0) === fId;
}


export default class TrackingPersistence {
  // constructor
  constructor(enqueueSnackbar, recordStore) {
    this.enqueueSnackbar = enqueueSnackbar;
    this.recordStore = recordStore;
    this.updateTrack = null;
    this.isEnabled = true;
    this.isConnected = true;
    this.trackCreationQueue = [];
    this.partPersistenceQueue = [];
    this.appInfo = null; // live-tracking-only
    this.resetTracking = null; // live-tracking-only
    this.user = null;

    this.trackProcessor();
    this.trackPartsProcessor();
  }


  // destructor
  destroy = () => {
    this.isEnabled = false;
  }


  // updates all possible tracks with saved track data
  updateSavedTrack = (id, trackData) => {
    // Don't update the time created. We need to preserve the timezone data and the server's
    // version might have different ideas.
    const { dateTimeCreated, ...dataToUpdate } = trackData;
    this.updateTrack(id, dataToUpdate);
  }


  // updates all possible tracks with saved point count
  updatePointCount = (id, batchCount) => {
    this.updateTrack(id, track => ({
      persistedPointCount: track.persistedPointCount + batchCount
    }));
  }


  // updates all possible tracks with saved marker data
  // TODO: don't double looks thru list
  updateSavedMarker = (id, markerData) => {
    this.updateTrack(id, track => ({
      ...track,
      markers: track.markers.map(m => m.uuid === markerData.uuid
        ? {
          ...m,
          id: markerData.id,
          photoUrl: markerData.photoUrl,
          // The photo preview will be displayed while the file is uploading.
          // A change in the marker's photo URL indicates that the files has finshed
          // uploading, so we can remove the preview. This assumes that changes to the
          // photo, e.g. selecting a new file or deleting the photo, will not happen
          // while a photo is uploading, which we enforce by disabling interaction
          // with the photo UI during upload.
          photoPreview: markerData.photoUrl !== m.photoUrl ? null : m.photoPreview,
        }
        : m
      )
    }));
  }


  // tracking request
  trackingRequest = (params) => {
    return axios({
      baseURL: process.env.REACT_APP_TRACKING_HOST,
      headers: this.appInfo 
        ? { 'x-device-unique-id': this.appInfo.uniqueId }
        : undefined,
      ...params,
    });
  }


  // unknown fatal request error.
  onFatalError = (track) => {
    this.enqueueSnackbar('This track has encountered an unrecoverable error and cannot continue.', {
      variant: 'error',
      persist: true,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'center'
      }
    });
    this.trackCreationQueue = this.trackCreationQueue.filter(t => t.id !== track.id);
    this.partPersistenceQueue = this.partPersistenceQueue.filter(t => t.id !== track.id);                
    this.resetTracking();
  }


  // processor for creating tracks so we have the track id required for persisting points & markers
  trackProcessor = async () => {
    while (this.isEnabled) {
      if (!this.isConnected || this.trackCreationQueue.length === 0) {
        await sleep(sleepTime);
      } else {
        const track = this.trackCreationQueue[0];
        const partsTrack = this.partPersistenceQueue.find(t => t.id === track.id);
        partsTrack.isCreatingTrack = true;
        let response = null;
        let trackData = null;
        
        try {
          // NOTE: data will be a response object if
          if (track.type === TRACK_TYPE.DEPLOYMENT) {
            ({ response, track: trackData } = await this.recordStore.createDeploymentTrack(deploymentTrackModel(track.params.dogId, track, this.appInfo)));
          } else if (track.type === TRACK_TYPE.LEAD) {
            ({ response, track: trackData } = await this.recordStore.createLeadTrack(leadTrackModel(track.params.name, track, this.appInfo)));
          } else if (track.type === TRACK_TYPE.FOLLOW) {
            ({ response, track: trackData } = await this.recordStore.createFollowTrack(followTrackModel(track.params.leadTrackId, track.params.exerciseId, track.params.dogId, track.params.isBlind, track, this.appInfo)));
            
            if (response?.status === 410) {
              // exercise id is invalid for given dog -> notify and repeat with null exer
              this.enqueueSnackbar('The specified exercise is unavailable. Following will continue with an auto-created exercise.', {
                variant: 'warning',
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'center'
                }
              });
              ({ response, track: trackData } = await this.recordStore.createFollowTrack(followTrackModel(track.params.leadTrackId, null, track.params.dogId, track.params.isBlind, track, this.appInfo)));
            } 
            
            if (response?.status === 409) {
              // lead track id has already been followed -> bail out of tracking and show error
              this.enqueueSnackbar('The specified laid track has already been followed and tracking cannot continue.', {
                variant: 'error',
                persist: true,
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'center'
                }
              });
              this.trackCreationQueue = this.trackCreationQueue.filter(t => t.id !== track.id);
              this.partPersistenceQueue = this.partPersistenceQueue.filter(t => t.id !== track.id);                
              this.resetTracking();
              continue;
            }
          }

          if (response.isSuccess && trackData) {
            // success
  
            // update the track, excluding points and markers
            const {points, markers, ...rest} = trackData;
            this.updateSavedTrack(track.id, rest);
  
            // remove from track creation queue
            this.trackCreationQueue.shift();
  
            // update point queue with returned saved data
            const pointQueueIndex = this.partPersistenceQueue.findIndex(t => t.id === track.id);
            if (pointQueueIndex !== -1) {
              // manually update id
              this.partPersistenceQueue[pointQueueIndex].id = trackData.id;
              extend(this.partPersistenceQueue[pointQueueIndex], trackData);
            } 
          } else if (response.status) {
            this.onFatalError(track);
          }
        } catch (e) {
          // fail -> capture exception
          console.error('Track creation exception', e);
          Sentry.captureMessage('Track creation exception', { extra: { 
            track: JSON.stringify(track), 
            exception: JSON.stringify(e) }});
        } 
      
        partsTrack.isCreatingTrack = false;

        if (!trackData) {
          // wait before trying again
          await sleep(sleepTime);
        }
      }
    }
  }


  // processor for persisting points & markers.
  // we only work on the first track in the queue, persisting its parts until there are none left and it has been marked complete.
  // at this point we move on to the next item in the queue.
  trackPartsProcessor = async () => {
    while (this.isEnabled) {
      try {
        const queueTrack = this.partPersistenceQueue[0];
        if (!this.isConnected || !queueTrack || !isPersisted(queueTrack)) {
          await sleep(sleepTime);
        } else {
          // process any points
          if (queueTrack.points.length > queueTrack.persistedPointIndex) {
            const points = queueTrack.points.slice(queueTrack.persistedPointIndex);
            try {
              const response = await this.savePoints(points, queueTrack.id);
              if (response.isSuccess) {
                queueTrack.persistedPointIndex += points.length;
                this.updatePointCount(queueTrack.id, points.length);
              }
            } catch (e) {
              if (!e.response) {
                // the server is unreachable so sleep for a bit and try again
                await sleep(sleepTime);
                continue;
              } else {
                // the server responded with a failure so stop trying and let the user know
                Sentry.captureMessage('Track point sync exception', { extra: { 
                  track: JSON.stringify(queueTrack),
                  points: JSON.stringify(points), 
                  exception: JSON.stringify(e) }});
                this.onFatalError(queueTrack);
                return;
              }
            }
          }
          
          // process any markers
          if (queueTrack.markers.length > 0) {
            // remove the marker from the queue before persisting so
            // the delete process will know that persistence is underway
            const marker = queueTrack.markers.shift();
            const success = await this.saveMarker(marker, queueTrack.id);
            if (!success) {
              // failure, -> put the marker back in the queue, unless a more recently-updated
              // one has been queued
              if (!queueTrack.markers.find(m => m.uuid === marker.uuid)) {
                queueTrack.markers.unshift(marker);
              }
              await sleep(sleepTime);
              continue;
            }
          }

          // check if we should attempt an action
          if (queueTrack.action === 'complete') {
            if (await this.completeTrack(queueTrack) === false) {
              await sleep(sleepTime);
            }
          }
          // check if we should attempt discard
          else if (queueTrack.action === 'discard') {
            if (await this.discardTrack(queueTrack) === false) {
              await sleep(sleepTime);
            }
          }
          // sleep here to prevent busy loop when track has nothing to sync but isn't yet complete.
          else if (!queueTrack.action && queueTrack.points.length === queueTrack.persistedPointIndex && queueTrack.markers.length === 0) {
            await sleep(sleepTime);
          }
        }
      }
      catch (e) {
        console.error('special exception', e);
        alert(`parts special ex: ${e.message}`);
      }
    }
  }



  // -----------------------------------------------------------------------------------------
  // track operations
  // -----------------------------------------------------------------------------------------


  // queue track for creation
  queueTrack = (track) => {
    // backup condition: remove possible stuck tracks in part queue if removal conditions failed to trigger
    // (or, as we're now doing, if we intentionally left the track in the persistence queue after saving to allow resumption)
    this.partPersistenceQueue = this.partPersistenceQueue.filter(t => !(t.points.length === t.persistedPointIndex && t.markers.length === 0));

    // copy track objects so we aren't operating on the same lists
    this.trackCreationQueue.push(cloneDeep(track));
    this.partPersistenceQueue.push(cloneDeep({ ...track, persistedPointIndex: 0 }));
  }


  // saves track stats to tracking and main APIs
  saveTrackStats = async (track) => {
    // track must be created before stats can be saved
    if (!isPersisted(track)) {
      return;
    }

    try {
      const filteredPoints = track.points; // these should be filtered
      const queueTrack = this.partPersistenceQueue.find(t => t.id === track.id);
      queueTrack.dateTimeStopped = track.dateTimeStopped;

      // assign promise to property so it can be awaited elsewhere
      queueTrack.savingStats = new Promise(async (resolve, reject) => {
        const durationSeconds = differenceInSeconds(track.dateTimeStopped ?? new Date(), track.dateTimeCreated);
        const distanceMeters = getTrackDistanceM(filteredPoints); // these should be filtered
        
        // call tracking API stats endpoint (to save distance, duration and completion status)
        let response = null;
        try {
          response = await this.trackingRequest({
            method: 'post',
            url: `/track/${track.id}/stats`,
            data: { distance: distanceMeters, duration: durationSeconds }
          });
        } catch {}

        if (response && !response.isSuccess && response.status) {
          this.onFatalError(track);
          return reject();
        } else if (!response || response?.isSuccess === false) {
          return reject();
        }

        // save main API summary stats
        try {
          if ([TRACK_TYPE.DEPLOYMENT, TRACK_TYPE.FOLLOW].includes(track.type)) {
            const trackTimeFormat = `yyyy-MM-dd'T'HH:mm:ss`;
            const durationMinutes = +(Math.max(durationSeconds / 60, 1).toFixed(0));
            const distanceMiles = convertDistance(distanceMeters, DISTANCE_UNIT_NAME.METERS, DISTANCE_UNIT_NAME.MILES);
            const distanceYards = convertDistance(distanceMeters, DISTANCE_UNIT_NAME.METERS, DISTANCE_UNIT_NAME.YARDS);
            const distanceImperial = distanceMiles > 0.6 ? distanceMiles : distanceYards;
            const distanceUnitImperial = { id: distanceMiles > 0.6 ? DISTANCE_UNIT_DDV.MILES : DISTANCE_UNIT_DDV.YARDS };
            const turnCount = getTurnCount(filteredPoints); // these should be filtered
            
            if (track.type === TRACK_TYPE.DEPLOYMENT) {
              track.deployment.sectionType = Types.DeploymentSection.PATROL;
              const tracking = track.deployment.patrol.patrolTypes[0].tracking;
              tracking.trackFollowedTime = format(new Date(track.dateTimeCreated), trackTimeFormat); // strip timezone
              tracking.trackDuration = durationMinutes;
              tracking.trackDistance = distanceImperial;
              tracking.trackDistanceUnit = distanceUnitImperial;
              tracking.trackTotalTurns = turnCount;
              const { record: deployment } = await this.recordStore.saveDeployment(track.deployment.id, track.deployment, track.deployment.date, true);
              if (!deployment) {
                return reject();
              }
            } else if (track.type === TRACK_TYPE.FOLLOW) {
              const ptRecord = track.completion.patrol.patrolTypes.find(pt => pt.tracking?.id === track.completionTrackingId);
              ptRecord.tracking.trackLayerName = track.leadTrack?.name ?? '';
              ptRecord.tracking.trackLaidTime = track.leadTrack ? format(new Date(track.leadTrack.dateTimeCreated), trackTimeFormat) : ''; // strip timezone
              ptRecord.tracking.trackFollowedTime = format(new Date(track.dateTimeCreated), trackTimeFormat); // strip timezone
              ptRecord.tracking.trackDuration = durationMinutes;
              ptRecord.tracking.trackDistance = distanceImperial;
              ptRecord.tracking.trackDistanceUnit = distanceUnitImperial;
              ptRecord.tracking.trackTotalTurns = turnCount;
              // use track-data-saving endpoint
              const response = await axios.put(`/training/answer/${track.completion.id}/track`, ptRecord);
              if (!response.isSuccess) {
                return reject();
              }
            }
          }
        } catch (err) {
          return reject();
        }
        
        resolve();
      });

      // await promise here so that exceptions in this scope are caught and ignored.
      // this promise is awaited in completeTrack when saving/completing a track so exceptions are handled there.
      await queueTrack.savingStats;
    } catch {}
  }


  // completes track (when save or complete is clicked).
  // this should ensure that a recent stats save has succeeded, 
  // otherwise it keeps retrying that as well until it has.
  completeTrack = async (track) => {
    const queueTrack = this.partPersistenceQueue.find(t => t.id === track.id);
    queueTrack.action = 'complete';

    // we can only complete if we have an id and no points/markers to persist and aren't already saving.
    if (!isPersisted(track)
      || queueTrack.persistedPointIndex < queueTrack.points.length 
      || queueTrack.markers.length > 0
      || queueTrack.isCreatingTrack
      || queueTrack.isCompleting
    ) {
      return false;
    }

    queueTrack.isCompleting = true;

    // wait for stat-saving to complete before proceeding.
    // if it fails (throws exception) then we bail and keep retrying.
    if (!queueTrack.savingStats) {
      await this.saveTrackStats(track);
    }
    if (queueTrack.savingStats) {
      try {
        await queueTrack.savingStats;
      } catch {
        delete queueTrack.savingStats;
        queueTrack.isCompleting = false;
        return false;
      }
    }

    // save completion status.
    let response = null;
    try {
      response = await this.trackingRequest({
        method: 'post',
        url: `/track/${track.id}/complete`,
      });
    } catch {}
    if (response && !response.isSuccess && response.status) {
      this.onFatalError(track);
      return false;
    } else if (!response?.isSuccess) {
      return false;
    }

    // remove from part-processing queue.
    this.partPersistenceQueue = this.partPersistenceQueue.filter(t => t.id !== track.id);
    queueTrack.isCompleting = false;
    queueTrack.action = null;
    return true;
  }


  // called when track is discarded. remove from queues and handle deletion of any auto-created records.
  discardTrack = async (track) => {
    // get track from parts queue
    let queueTrack = this.partPersistenceQueue.find(t => t.id === track.id);
    if (!queueTrack) {
      queueTrack = track;
    }

    queueTrack.action = 'discard';

    if (queueTrack.isCreatingTrack || queueTrack.isDiscarding) {
      return false;
    }

    queueTrack.isDiscarding = true;

    // delete possible created record from main API
    if (track.createdRecord) {
      let response = null;
      let deleteStatus = null; // success | fail-stop | fail-retry
      try {
        if (track.createdRecord.recordType === Types.Record.DEPLOYMENT) {
          // prevent discard/delete if deployment was already completed
          response = await axios.get(`/deployment/${track.createdRecord.id}`);
          if (response.isSuccess && response.data.isComplete) {
            deleteStatus = DELETE_RECORD_STATUS.FAIL_STOP;
            response = null;
            this.enqueueSnackbar('Unable to discard this track as the deployment is complete.', {
              variant: 'error',
              persist: true,
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'center'
              }
            });
          } else {
            ({ response } = await this.recordStore.deleteDeployment(track.createdRecord.id));
          }
        } else if (track.createdRecord.recordType === Types.Record.EVENT) {
          ({ response } = await this.recordStore.deleteEvent(track.createdRecord.id));
        } else if (track.createdRecord.recordType === Types.Record.EXERCISE) {
          ({ response } = await this.recordStore.deleteExercise(track.createdRecord.id));
        } else if (track.createdRecord.type === 'completion-tracking-record') {
          // no new first-class record was created, only the tracking portion of an existing exercise.
          // use the special endpoint for clearing that.
          response = await axios.delete(`/training/answer/${track.createdRecord.completionId}/track/${track.createdRecord.id}`);
        }

        // if we have a response with a failure then server can't delete and we should not retry.
        if (response) {
          deleteStatus = response.isSuccess
            ? DELETE_RECORD_STATUS.SUCCESS
            : DELETE_RECORD_STATUS.FAIL_STOP;
        }
      } catch {
        // if we have no response then the delete failed but we should retry.
        deleteStatus = Boolean(response)
          ? DELETE_RECORD_STATUS.FAIL_STOP
          : DELETE_RECORD_STATUS.FAIL_RETRY;
      }

      if (deleteStatus === DELETE_RECORD_STATUS.FAIL_STOP) {
        queueTrack.isDiscarding = false;
        queueTrack.action = null;
        return false;
      } else if (deleteStatus === DELETE_RECORD_STATUS.FAIL_RETRY) {
        queueTrack.isDiscarding = false;
        return false;
      }
    }

    // delete possible created record from tracking API
    if (isPersisted(track)) {
      let response = null;
      try {
        if (track.type === TRACK_TYPE.DEPLOYMENT) {
          response = await this.trackingRequest({ method: 'delete', url: `/deployment-track/${track.id}` });
        } else if (track.type === TRACK_TYPE.LEAD) {
          response = await this.trackingRequest({ method: 'delete', url: `/exercise-lead-track/${track.id}` });
        } else if (track.type === TRACK_TYPE.FOLLOW) {
          response = await this.trackingRequest({ method: 'delete', url: `/exercise-follow-track/${track.id}` });
        }
        // server error response
        if (response && !response.isSuccess && response.status !== 404) {
          this.onFatalError(track);
          queueTrack.isDiscarding = false;
          return false;
        }
      } catch {
        queueTrack.isDiscarding = false;
        return false;
      }
    }

    this.trackCreationQueue = this.trackCreationQueue.filter(t => t.id !== track.id);
    this.partPersistenceQueue = this.partPersistenceQueue.filter(t => t.id !== track.id);
    return true;
  }



  // -----------------------------------------------------------------------------------------
  // point operations
  // -----------------------------------------------------------------------------------------


  // queue point for persistence
  queuePoint = (id, point) => {
    const queueTrack = this.partPersistenceQueue.find(track => track.id === id);
    if (!queueTrack) {
      console.error('Point queued for persistence with no track found. Race condition?', id, this.partPersistenceQueue);
      return;
    }

    queueTrack.points.push(point);
  }


  // performs call to persist given points
  savePoints = (points, trackId) => {
    return this.trackingRequest({
      method: 'post',
      url: `/track/${trackId}/points`,
      data: { points }
    });
  }



  // -----------------------------------------------------------------------------------------
  // marker operations
  // -----------------------------------------------------------------------------------------


  // queue marker for persistence
  queueMarker = (track, marker) => {
    // We don't want to submit photoPreview
    const { photoPreview, ...markerToQueue } = marker;

    const queueTrack = this.partPersistenceQueue.find(t => t.id === track.id);
    if (queueTrack) {
      const markerIdx = queueTrack.markers.findIndex(m => m.uuid === markerToQueue.uuid);
      if (markerIdx >= 0) {
        // the marker is already in the persistence queue -> update it there
        queueTrack.markers[markerIdx] = markerToQueue;
      } else {
        // the marker is not in the persistence queue -> add it
        queueTrack.markers.push(markerToQueue);
      }
    } else if (isPersisted(track)) {
      // If there's no queued track for part persistence but the track has been saved, we're not in
      // queue mode, so just save.
      this.saveMarker(markerToQueue, track.id)
    } else {
      console.error('Marker queued for persistence with no track found. Race condition?', track.id, this.partPersistenceQueue);
    }
  }


  // removes marker from the queue
  dequeueMarker = (track, marker) => {
    const queueTrack = this.partPersistenceQueue.find(t => t.id === track.id);
    if (queueTrack) {
      const markerIdx = queueTrack.markers.findIndex(m => m.uuid === marker.uuid);
      if (markerIdx >= 0) {
        queueTrack.markers.splice(markerIdx, 1);        
      }
    }
  }


  // performs call to persist given marker
  saveMarker = async (marker, trackId = null) => {
    // check for delete flag
    const deletingMarker = !!marker.delete;

    try {
      let response;

      if (deletingMarker) {
        response = await this.trackingRequest({ method: 'delete', url: `/track/marker/${marker.id}` });        
      } else {
        const formData = objectToFormData(marker, '', []);
        response = await this.trackingRequest({
          method: marker.id ? 'put' : 'post',
          url: marker.id ? `/track/marker/${marker.id}` : `/track/${trackId}/marker`,
          data: formData
        });
      }

      if (response.isSuccess) {
        // success -> call all registered callbacks
        if (!deletingMarker) {
          this.updateSavedMarker(trackId, { ...marker, ...response.data });
        }
        return true;
      }
    } catch (err) {
      if (err.response?.status === 404) {
        // marker may have been previously deleted
        console.error(`Marker not found for ${deletingMarker ? 'delete' : 'update'}`, marker.id);
        return false;
      }
    }

    return false;
  }


  // delete marker
  deleteMarker = async (track, marker) => {
    if (!marker.id) {
      // the marker hasn't been persisted yet -> just remove it from the queue
      this.dequeueMarker(track, marker);
    } else {
      // the marker has been persisted -> queue it with a delete flag
      this.queueMarker(track, { ...marker, delete: true })
    }
  }; 
}